import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Dumbbell,
  Mountain,
  Bike,
  Fish,
  Palette,
  Gamepad2,
  Heart,
  Award,
  Settings,
  Users,
  Calendar,
  Star,
  Globe,
  Shield,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  Copy,
  MapPin,
  Phone,
  Mail,
  Layout,
} from 'lucide-react';
import axios from 'axios';
import AdminSportsVerification from './AdminSportsVerification';
import AdminPlacementControls from './AdminPlacementControls';
import AdminOutdoorCamping from './AdminOutdoorCamping';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Subcategory configurations
const SUBCATEGORY_CONFIG = {
  'sports-equipment': { name: 'Sports Equipment', icon: '🏅', lucideIcon: Award, color: 'blue' },
  'fitness-gym': { name: 'Fitness & Gym', icon: '🏋️', lucideIcon: Dumbbell, color: 'red' },
  'outdoor-camping': { name: 'Outdoor & Camping', icon: '🏕️', lucideIcon: Mountain, color: 'green' },
  'cycling-equipment': { name: 'Cycling Equipment', icon: '🚴', lucideIcon: Bike, color: 'cyan' },
  'fishing-hunting': { name: 'Fishing & Hunting', icon: '🎣', lucideIcon: Fish, color: 'teal' },
  'yoga-wellness': { name: 'Yoga & Wellness', icon: '🧘', lucideIcon: Heart, color: 'purple' },
  'board-games-puzzles': { name: 'Board Games & Puzzles', icon: '🎲', lucideIcon: Gamepad2, color: 'amber' },
  'creative-hobbies': { name: 'Creative Hobbies', icon: '🎨', lucideIcon: Palette, color: 'rose' },
};

const AdminSportsServices = () => {
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('settings');
  
  // Data
  const [settings, setSettings] = useState(null);
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialogs
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  
  // Form state for new provider
  const [newProvider, setNewProvider] = useState({
    provider_name: '',
    business_name: '',
    subcategory: 'sports-equipment',
    service_types: [],
    location: '',
    city: '',
    phone: '',
    email: '',
    starting_price: '',
    services: [],
  });

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, providersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sports-services/admin/settings/${selectedCountry}`),
        axios.get(`${API_URL}/api/sports-services/admin/providers?country=${selectedCountry}&include_inactive=true`),
        axios.get(`${API_URL}/api/sports-services/admin/stats?country=${selectedCountry}`),
      ]);
      
      setSettings(settingsRes.data);
      setProviders(providersRes.data.providers || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch sports services data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/sports-services/admin/settings/${selectedCountry}`, settings);
      alert('Settings saved successfully!');
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSubcategory = (subcategory, enabled) => {
    setSettings(prev => ({
      ...prev,
      subcategories: {
        ...prev.subcategories,
        [subcategory]: {
          ...prev.subcategories?.[subcategory],
          enabled
        }
      }
    }));
  };

  const handleToggleProvider = async (providerId) => {
    try {
      const res = await axios.put(`${API_URL}/api/sports-services/admin/providers/${providerId}/toggle`);
      setProviders(prev => prev.map(p => 
        p.id === providerId ? { ...p, is_active: res.data.is_active } : p
      ));
    } catch (err) {
      alert('Failed to toggle provider');
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/sports-services/admin/providers/${providerId}`);
      setProviders(prev => prev.filter(p => p.id !== providerId));
    } catch (err) {
      alert('Failed to delete provider');
    }
  };

  const handleAddProvider = async () => {
    try {
      const providerData = {
        ...newProvider,
        country: selectedCountry,
        starting_price: parseFloat(newProvider.starting_price) || 0,
      };
      
      const res = await axios.post(`${API_URL}/api/sports-services/admin/providers`, providerData);
      setProviders(prev => [res.data.provider, ...prev]);
      setShowAddProvider(false);
      setNewProvider({
        provider_name: '',
        business_name: '',
        subcategory: 'sports-equipment',
        service_types: [],
        location: '',
        city: '',
        phone: '',
        email: '',
        starting_price: '',
        services: [],
      });
    } catch (err) {
      alert('Failed to add provider');
    }
  };

  const handleCloneFromCountry = async (sourceCountry) => {
    if (!confirm(`Clone settings from ${sourceCountry}? This will overwrite current settings.`)) return;
    
    try {
      await axios.post(`${API_URL}/api/sports-services/admin/settings/${selectedCountry}/clone-from/${sourceCountry}`);
      await fetchData();
      alert('Settings cloned successfully!');
    } catch (err) {
      alert('Failed to clone settings');
    }
  };

  const handleSeedDemoData = async () => {
    if (!confirm('Seed demo providers? This will replace existing providers.')) return;
    
    try {
      await axios.post(`${API_URL}/api/sports-services/seed-demo-providers?country=${selectedCountry}`);
      await fetchData();
      alert('Demo data seeded successfully!');
    } catch (err) {
      alert('Failed to seed demo data');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            🏅 Sports, Fitness & Hobbies
          </h2>
          <p className="text-gray-500">Manage services for sports and fitness categories</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 UK</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_providers}</div>
              <p className="text-sm text-gray-500">Total Providers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">{stats.active_providers}</div>
              <p className="text-sm text-gray-500">Active Providers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total_bookings}</div>
              <p className="text-sm text-gray-500">Total Bookings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.completion_rate}%</div>
              <p className="text-sm text-gray-500">Completion Rate</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-1" />
            Country Settings
          </TabsTrigger>
          <TabsTrigger value="verification" data-testid="verification-tab">
            <ShieldCheck className="h-4 w-4 mr-1" />
            Verification
          </TabsTrigger>
          <TabsTrigger value="placement" data-testid="placement-tab">
            <Layout className="h-4 w-4 mr-1" />
            Placement
          </TabsTrigger>
          <TabsTrigger value="outdoor" data-testid="outdoor-camping-tab">
            <Mountain className="h-4 w-4 mr-1" />
            Outdoor
          </TabsTrigger>
          <TabsTrigger value="providers">
            <Users className="h-4 w-4 mr-1" />
            Providers ({providers.length})
          </TabsTrigger>
          <TabsTrigger value="bookings">
            <Calendar className="h-4 w-4 mr-1" />
            Bookings
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Country Settings - {selectedCountry}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleCloneFromCountry(selectedCountry === 'SL' ? 'UK' : 'SL')}>
                    <Copy className="h-4 w-4 mr-1" />
                    Clone from {selectedCountry === 'SL' ? 'UK' : 'SL'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category Enable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Sports Services Module</p>
                  <p className="text-sm text-gray-500">Enable sports services for {selectedCountry}</p>
                </div>
                <Switch
                  checked={settings?.category_enabled !== false}
                  onCheckedChange={(v) => setSettings(prev => ({ ...prev, category_enabled: v }))}
                />
              </div>

              {/* Subcategory Panel */}
              <div className="space-y-3">
                <h4 className="font-medium text-lg">Subcategory Controls</h4>
                {Object.entries(SUBCATEGORY_CONFIG).map(([key, config]) => {
                  const subcatSettings = settings?.subcategories?.[key] || {};
                  const Icon = config.lucideIcon;
                  const providerCount = stats?.providers_by_subcategory?.[key] || 0;
                  
                  return (
                    <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-${config.color}-100`}>
                          <Icon className={`h-5 w-5 text-${config.color}-600`} />
                        </div>
                        <div>
                          <p className="font-medium">{config.icon} {config.name}</p>
                          <p className="text-xs text-gray-500">
                            {providerCount} providers · Min Level: {subcatSettings.min_verification_level || 1}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={subcatSettings.enabled !== false ? 'default' : 'secondary'}>
                          {subcatSettings.enabled !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Switch
                          checked={subcatSettings.enabled !== false}
                          onCheckedChange={(v) => handleToggleSubcategory(key, v)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verification Tab */}
        <TabsContent value="verification" className="mt-4">
          <AdminSportsVerification />
        </TabsContent>

        {/* Placement Controls Tab */}
        <TabsContent value="placement" className="mt-4">
          <AdminPlacementControls />
        </TabsContent>

        {/* Outdoor & Camping Tab */}
        <TabsContent value="outdoor" className="mt-4">
          <AdminOutdoorCamping country={selectedCountry} />
        </TabsContent>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">Service Providers</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSeedDemoData}>
                Seed Demo Data
              </Button>
              <Dialog open={showAddProvider} onOpenChange={setShowAddProvider}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Provider
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New Provider</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Provider Name *</Label>
                        <Input
                          value={newProvider.provider_name}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, provider_name: e.target.value }))}
                          placeholder="Provider name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Business Name</Label>
                        <Input
                          value={newProvider.business_name}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, business_name: e.target.value }))}
                          placeholder="Business name"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Subcategory *</Label>
                      <Select 
                        value={newProvider.subcategory} 
                        onValueChange={(v) => setNewProvider(prev => ({ ...prev, subcategory: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(SUBCATEGORY_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              {config.icon} {config.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Input
                          value={newProvider.city}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="City"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Starting Price</Label>
                        <Input
                          type="number"
                          value={newProvider.starting_price}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, starting_price: e.target.value }))}
                          placeholder="e.g., 3000"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={newProvider.phone}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="07X XXX XXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={newProvider.email}
                          onChange={(e) => setNewProvider(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    
                    <Button className="w-full" onClick={handleAddProvider} disabled={!newProvider.provider_name || !newProvider.city}>
                      Add Provider
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Provider Table */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium">Provider</th>
                    <th className="text-left p-3 text-sm font-medium">Subcategory</th>
                    <th className="text-left p-3 text-sm font-medium">Location</th>
                    <th className="text-left p-3 text-sm font-medium">Rating</th>
                    <th className="text-left p-3 text-sm font-medium">Status</th>
                    <th className="text-right p-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {providers.map((provider) => {
                    const config = SUBCATEGORY_CONFIG[provider.subcategory] || {};
                    return (
                      <tr key={provider.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{provider.provider_name}</p>
                            <p className="text-xs text-gray-500">{provider.business_name}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">
                            {config.icon} {config.name}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <span className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3" />
                            {provider.city}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            {provider.rating?.toFixed(1) || 'N/A'}
                            <span className="text-gray-400 text-xs">({provider.review_count || 0})</span>
                          </span>
                        </td>
                        <td className="p-3">
                          <Switch
                            checked={provider.is_active}
                            onCheckedChange={() => handleToggleProvider(provider.id)}
                          />
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500"
                              onClick={() => handleDeleteProvider(provider.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {providers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-gray-500">
                        No providers found. Add a provider or seed demo data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-500 text-center py-8">
                Booking management coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSportsServices;
